import type {
  IAccessRoute,
  IAmenity,
  IDtoListingBooking,
  IDtoListingLocation,
  IDtoPotentialHazards,
  IDtoSiteName,
  IListing,
  IListingExtra,
  ILandOwner,
  INoise,
  IPolicy,
  IPrimaryPropertyType,
  IPrivacy,
  IPropertyDescriptor,
  IRoadConditionDescribed,
  ISubPhoto,
  IVehicle,
} from '@curbnturf/entities';
import { HelperFunctions } from '@curbnturf/entities';
import type { Amenity, CancellationTier, IAddOn, RvType } from './mock-listings.data';
import { RV_TYPES } from './mock-listings.data';
import {
  RESERVED_ADDON_TYPE_KEYS,
  addOnCapFromCount,
  addOnDisplayName,
  addOnUnitFromReal,
  addOnUnitToReal,
} from './add-on-pricing.util';
import type {
  IDraftListing,
  VisibilityKind,
  NoiseLevel,
  RoadCondition,
  CheckInProcess,
  Bookability,
  IHouseRules,
  IPhotoMeta,
  PrimaryPropertyType,
  PropertyDescriptor,
  ElectricityAmps,
  WaterHookup,
} from './draft-listing.types';

/**
 * Maps the host wizard's IDraftListing onto @curbnturf/objects' real IListing
 * shape. Split from host-listing-draft.service.ts so the field-by-field
 * mapping is independently readable/testable — the service just calls these
 * and drives the multi-call create/update sequence.
 *
 * The server's `addCoordinates()` step (listing.service.ts) runs a reverse
 * geocode whenever address.city/state are missing while coordinates are
 * present, and a forward geocode the other way around — sending both
 * together in the same call risks tripping it depending on exactly which
 * fields the entity constructor sees first. The proven-safe pattern (used by
 * curbnturf's own sign-up-state effects) is a BARE create (`{ user }` only),
 * then location/booking/description/hazards set via their own dedicated
 * PUT endpoints — so each field group only ever changes one thing at a time.
 *
 * Known lossy mappings (objects' model is coarser than the wizard's):
 *  - checkInProcess: wizard has 3 values (meet-greet/self-checkin/lockbox),
 *    IListing only has 2 (meetAndGreet/none) — self-checkin and lockbox both
 *    collapse to 'none' (closest available meaning: no meet-and-greet required).
 *  - extras (add-ons): IListingExtra only has a flat `price` — no `icon`,
 *    `unit` (per-night/per-person/per-unit), or `photo` concept, so those are
 *    dropped. `description` maps 1:1.
 *  - tentMode: the wizard has three states (tents-only / allowed alongside RVs /
 *    not accepted) but the server has a single `tentOnly` boolean and no concept
 *    of "tents allowed as well as RVs". So 'tents-only' and undefined round-trip,
 *    and **'allowed' is lost** — it publishes as "tents not accepted". That needs a
 *    server column to fix properly and is written up in the fixes doc; it is not
 *    worked around here, because the alternative (setting tentOnly for 'allowed')
 *    would tell guests the site takes no RVs, which is worse than losing the flag.
 *  - amenities: electricity amp rating and water hookup type ('direct'|'station')
 *    round-trip losslessly via `IAmenity.options` (`draftToRealSiteDetails`/
 *    `realListingToDraft`, one electricity row per selected amp). Sewer hookup type
 *    (full-hookup vs. dump) has no `options` value — only presence of the amenities.
 */

function mapCancellationPolicy(
  tier: IDraftListing['cancellationTier'],
): 'easy' | 'moderate' | 'strict' | 'exclusive' | undefined {
  if (!tier) return undefined;
  return tier === 'easy-goin' ? 'easy' : tier;
}

function mapReservationProcess(bookability: IDraftListing['bookability']): 'standard' | 'instant' {
  return bookability === 'instant' ? 'instant' : 'standard';
}

function mapCheckInProcess(process: IDraftListing['checkInProcess']): 'meetAndGreet' | 'none' | undefined {
  if (!process) return undefined;
  return process === 'meet-greet' ? 'meetAndGreet' : 'none';
}

function mapPrivacy(visibility: IDraftListing['visibility']): IPrivacy {
  const v = new Set(visibility ?? []);
  return {
    secluded: v.has('secluded'),
    home: v.has('home-visible'),
    neighbor: v.has('neighbors'),
    others: v.has('other-guests'),
  };
}

function mapNoise(noiseLevel: IDraftListing['noiseLevel']): INoise {
  // Server's site-isolation handler calls `data.noise.level.toLowerCase()`
  // unconditionally (no null-check) — always send a real level, never leave
  // it undefined, or that call throws a 500 rather than a clean validation error.
  if (!noiseLevel) return { level: 'silence' };
  return { level: noiseLevel === 'sounds-of-nature' ? 'nature' : noiseLevel };
}

const ROAD_CONDITION_KEY: Record<RoadCondition, keyof IRoadConditionDescribed> = {
  paved: 'paved',
  'smooth-gravel': 'smoothGravel',
  'poor-gravel': 'poorGravel',
  'high-clearance': 'highClearance',
  'off-road': 'offRoad',
};

function mapRoadCondition(roadConditions: RoadCondition[]): IRoadConditionDescribed {
  const condition: IRoadConditionDescribed = {
    paved: false,
    smoothGravel: false,
    poorGravel: false,
    highClearance: false,
    offRoad: false,
  };
  for (const roadCondition of roadConditions ?? []) {
    condition[ROAD_CONDITION_KEY[roadCondition]] = true;
  }
  return condition;
}

function mapLandOwner(draft: IDraftListing): ILandOwner {
  if (draft.isLandowner === false) {
    return {
      self: false,
      private: draft.landType === 'private',
      represent: draft.representsLandowner,
      firstName: draft.landowner?.firstName,
      lastName: draft.landowner?.lastName,
      phone: draft.landowner?.phone,
      address: draft.landowner?.address,
    };
  }
  return { self: true };
}

/** IDtoListingLocation — address + coordinates + landOwner, set via PUT /listing/:id/location. */
export function draftToRealListingLocation(draft: IDraftListing): IDtoListingLocation {
  return {
    address: draft.address ?? { address: '', city: '', state: '', zipcode: '' },
    coordinates: { lat: draft.lat ?? 0, lon: draft.lng ?? 0 },
    landOwner: mapLandOwner(draft),
  };
}

/** IDtoListingBooking — check-in/out, stay-length, cancellation — PUT /listing/:id/booking. */
export function draftToRealListingBooking(draft: IDraftListing): IDtoListingBooking {
  return {
    checkIn: draft.checkInTime,
    checkOut: draft.checkOutTime,
    minLengthOfStayInDays: draft.minNights,
    maxLengthOfStayInDays: draft.maxNights,
    reserveDaysInAdvance: draft.minNoticeHours != null ? Math.ceil(draft.minNoticeHours / 24) : undefined,
    bookingWindow: draft.maxNoticeWeeks != null ? draft.maxNoticeWeeks * 7 : undefined,
    checkInProcess: mapCheckInProcess(draft.checkInProcess),
    reservationProcess: mapReservationProcess(draft.bookability),
    cancellationPolicy: mapCancellationPolicy(draft.cancellationTier),
  };
}

/**
 * F21. Edit saves re-send the whole booking section, so a field the host never
 * touched is still written back — and the server does not store every value it
 * is sent. `allowedReservationProcess()` (server listing.service.ts) quietly
 * stores 'standard' when the owner lacks `instantBookingAllowed`, so re-sending
 * an unchanged 'instant' turned a grandfathered Instant Book listing into
 * request-to-book. The server only touches either field when it is present in
 * the payload, so the fix is to leave out whatever already matches the live
 * listing. Check-in compares case-insensitively: rows store 'meetandgreet'.
 */
export function omitUnchangedBooking(payload: IDtoListingBooking, live: IListing): IDtoListingBooking {
  const out = { ...payload };
  if (live.reservationProcess && out.reservationProcess === live.reservationProcess) {
    delete out.reservationProcess;
  }
  if (live.checkInProcess && out.checkInProcess?.toLowerCase() === String(live.checkInProcess).toLowerCase()) {
    delete out.checkInProcess;
  }
  return out;
}

/** IDtoSiteName reused for the listing's own name+description — PUT /listing/:id/description. */
export function draftToRealListingDescription(draft: IDraftListing): IDtoSiteName {
  return { name: draft.title ?? '', description: draft.description, available: true };
}

/** IDtoPotentialHazards — PUT /listing/:id/potential-hazards. */
export function draftToRealPotentialHazards(draft: IDraftListing): IDtoPotentialHazards {
  return { potentialHazards: !!draft.hasHazards, potentialHazardDescription: draft.hazardsNote ?? '' };
}

/** The wizard's rule checkboxes and the policy sentence each one is saved as.
 *  `reverseRules` matches on these exact sentences, so changing one means
 *  existing listings read that rule back as free text. */
const RULE_SENTENCES: ReadonlyArray<[keyof IHouseRules, string]> = [
  ['noSmoking', 'No smoking on the property'],
  ['noParties', 'No parties or events'],
  ['quietHours', 'Quiet hours 10 PM – 7 AM'],
  ['noFireworks', 'No fireworks on the premises'],
  ['noFirearms', 'No firearms on the premises'],
];

/** House rules the host set in the wizard, as real `IPolicy[]` — PUT /listing/:id/policies.
 *  Mirrors `houseRulesFromDraft()`'s same boolean-flags + free-text expansion (mock-listings.data.ts). */
export function draftToRealListingPolicies(draft: IDraftListing): IPolicy[] {
  const descriptions: string[] = [];
  for (const [key, sentence] of RULE_SENTENCES) {
    if (draft.rules?.[key]) descriptions.push(sentence);
  }
  for (const line of (draft.customRules ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)) {
    descriptions.push(line);
  }
  return descriptions.map((description): IPolicy => ({ description, custom: true }));
}

/** ISite content: amenities + rig-size caps + guest capacity — IDtoSiteDetails shape. */
export function draftToRealSiteDetails(draft: IDraftListing): {
  amenities: IAmenity[];
  maxWidth?: number;
  maxLength?: number;
  maxHeight?: number;
  maxGuests: number;
  tentOnly: boolean;
} {
  const amenities: IAmenity[] = (draft.amenities ?? []).flatMap((type) => {
    if (type === 'electricity' && draft.electricityAmps && draft.electricityAmps.length > 0) {
      // One row per selected amp rating — a site can offer more than one pedestal amp
      // (e.g. both 30A and 50A); options carries the bare numeric value, matching the
      // vocabulary ListingAmenitiesComponent/amenity-display.component.ts already use.
      return draft.electricityAmps.map((amp) => ({ type, options: amp.replace('A', '') }));
    }
    if (type === 'water' && draft.waterHookup) return [{ type, options: draft.waterHookup }];
    return [{ type }];
  });
  return {
    amenities,
    maxWidth: draft.maxRig?.width,
    maxLength: draft.maxRig?.length,
    maxHeight: draft.maxRig?.height,
    maxGuests: draft.guestCapacity ?? 1,
    // `tentOnly` lives on the DETAILS payload, not on vehicles — and it is written
    // with a plain `tentOnly: details.tentOnly` into a query-builder update
    // (`server/src/site/site.service.ts`), so passing `undefined` would null the
    // column rather than leave it alone. Always send an explicit boolean.
    tentOnly: draft.tentMode === 'tents-only',
  };
}

/**
 * ISite name content — `IDtoSiteName` for `PUT /site/:id/name` (F15).
 *
 * The wizard provisions exactly one site per listing and never named it, so the
 * row kept whatever the server defaulted while `canEnable()` requires a name.
 * The listing's own title is the most useful thing to show in the post-publish
 * sites editor; `Site 1` is the fallback because the server rejects an empty
 * name with a 400 rather than defaulting it.
 *
 * It leaves out `available`: the site goes live through the listing's publish,
 * which runs the server's checks, and an edit must not change it (F21).
 */
export function draftToRealSiteName(draft: IDraftListing): IDtoSiteName {
  return {
    name: draft.title?.trim() || 'Site 1',
    description: draft.description ?? '',
  };
}

/**
 * ISite vehicle content — the `IVehicle` shape `PUT /site/:id/vehicles` takes (F15).
 *
 * The wizard collects this in phase 1 step 5 and `stepValidities()` index 4 will not
 * let the step complete without an answer, but `fillSiteContent()` never sent it, so
 * the published site carried whatever the server defaulted. `testVehicles()` on the
 * server (`site.service.ts`) rejects a site with no tentOnly and all eight false, so
 * publishes were only getting through on that default.
 *
 * Two details worth keeping:
 *  - `RvType`'s eight members are exactly `IVehicle`'s eight keys, so this is a
 *    presence check per key, not a translation table.
 *  - The server wraps the body in `new Vehicle(data)`, which coerces every key with
 *    `!!` — it is a full replace, not a patch. Every key is therefore sent explicitly
 *    rather than relying on omission meaning false.
 *
 * `tents-only` clears the RV set in the step component, so it correctly yields all
 * eight false; the `tentOnly` flag on the details payload is what carries that.
 */
export function draftToRealSiteVehicles(draft: IDraftListing): IVehicle {
  const chosen = new Set(draft.vehicleTypes ?? []);
  const tentsOnly = draft.tentMode === 'tents-only';
  return RV_TYPES.reduce((acc, { id }) => {
    acc[id] = !tentsOnly && chosen.has(id);
    return acc;
  }, {} as IVehicle);
}

/** ISite isolation content — IDtoSiteIsolation shape. */
export function draftToRealSiteIsolation(draft: IDraftListing): {
  noise: INoise;
  privacy: IPrivacy;
  accessRoutes: IAccessRoute[];
  potentialHazards: boolean;
  potentialHazardDescription: string;
} {
  return {
    noise: mapNoise(draft.noiseLevel),
    privacy: mapPrivacy(draft.visibility),
    accessRoutes: draft.roadConditions?.length ? [{ roadCondition: mapRoadCondition(draft.roadConditions) }] : [],
    potentialHazards: !!draft.hasHazards,
    // Server calls `.toString()` on this with no null-check — always send a
    // real string, never undefined (see mapNoise's comment, same class of bug).
    potentialHazardDescription: draft.hazardsNote ?? '',
  };
}

/**
 * IListingExtra[] for `PUT /listing/:id/extras/` — a full-replace endpoint
 * (send the entire current set every time, not a delta).
 *
 * The two `label`s here mean different things, and conflating them was a real
 * defect (Batch A #6). On the marketing `IAddOn`, `label` IS the guest-facing
 * name. On the server's `IListingExtra` it is a reserved TYPE key —
 * `@ApiProperty example: 'extraVehicle'` — while `name` is the column
 * documented "A name shown to guests". This used to write the host's typed
 * text into `label` and never populate `name` at all, with two consequences:
 *
 *  - A host who happened to type a reserved word got silently renamed. Reading
 *    back, `addOnsFromEntity` looks up `RESERVED_EXTRA_LABELS[e.label]` first,
 *    so `firewood` came back as `Firewood` while `tour` stayed lowercase. Same
 *    table, two behaviours, which is exactly what listing 38 shows today.
 *  - Nothing could ever distinguish a host's custom extra from a reserved one.
 *
 * So: the guest-facing text goes to `name`, and `label` carries the type key
 * when there is one. `count` is the server's "maximum number that can be
 * purchased" — a stock cap, not a guest-selectable quantity — and is now sent
 * so a cap the host set survives publish instead of being dropped. `unit` is
 * sent too, so "per night" and "per person" survive publish.
 */
export function draftToRealListingExtras(draft: IDraftListing): IListingExtra[] {
  return (draft.addOns ?? []).map((a) => ({
    label: RESERVED_ADDON_TYPE_KEYS[a.label?.trim().toLowerCase() ?? ''] ?? '',
    name: a.label,
    description: a.description,
    price: a.price,
    unit: addOnUnitToReal(a.unit),
    ...(typeof a.maxQuantity === 'number' && a.maxQuantity > 0 ? { count: a.maxQuantity } : {}),
  }));
}

// ─────────────────────── primaryType/descriptors: wizard (kebab-case) ↔ real (camelCase) ───────────────────────
//
// Two different enum vocabularies for the same concept — now a clean, total
// bijection in both directions (13 primary types, 17 descriptors, including
// 'driveway' as a descriptor on both sides — the real backend used to treat
// it as a primary type, a legacy artifact from the old ILandType vocabulary
// the wizard never shared; see objects/src/enums/category-types.ts).
// customPrimaryLabel (free text for primaryType: 'custom') also round-trips
// via its own real `IListing.customPrimaryLabel` column.

const PRIMARY_TYPE_TO_REAL: Record<PrimaryPropertyType, IPrimaryPropertyType['type']> = {
  winery: 'winery',
  brewery: 'brewery',
  distillery: 'distillery',
  farm: 'farm',
  'specialty-farm': 'specialtyFarm',
  orchard: 'orchard',
  ranch: 'ranch',
  'golf-course': 'golfCourse',
  church: 'church',
  'rv-park': 'rvPark',
  woodlands: 'woodlands',
  'private-home': 'privateHome',
  custom: 'custom',
};

/** Reverse of PRIMARY_TYPE_TO_REAL — full, every real primary type has a wizard equivalent. */
const REAL_PRIMARY_TYPE_TO_WIZARD: Record<IPrimaryPropertyType['type'], PrimaryPropertyType> = Object.fromEntries(
  (Object.entries(PRIMARY_TYPE_TO_REAL) as [PrimaryPropertyType, IPrimaryPropertyType['type']][]).map(
    ([wizard, real]) => [real, wizard],
  ),
) as Record<IPrimaryPropertyType['type'], PrimaryPropertyType>;

const DESCRIPTOR_TO_REAL: Record<PropertyDescriptor, IPropertyDescriptor['type']> = {
  backyard: 'backyard',
  curbside: 'curbside',
  driveway: 'driveway',
  beach: 'beach',
  coastal: 'coastal',
  lake: 'lake',
  'river-stream': 'stream',
  mountains: 'mountains',
  desert: 'desert',
  canyon: 'canyon',
  forest: 'forest',
  'hot-spring': 'hotSpring',
  waterfall: 'waterfall',
  wetlands: 'wetlands',
  'plain-prairie': 'plain',
  urban: 'urban',
  suburban: 'suburban',
};

/** Reverse of DESCRIPTOR_TO_REAL — full, every real descriptor has a wizard equivalent. */
const REAL_DESCRIPTOR_TO_WIZARD: Record<IPropertyDescriptor['type'], PropertyDescriptor> = Object.fromEntries(
  (Object.entries(DESCRIPTOR_TO_REAL) as [PropertyDescriptor, IPropertyDescriptor['type']][]).map(([wizard, real]) => [
    real,
    wizard,
  ]),
) as Record<IPropertyDescriptor['type'], PropertyDescriptor>;

/**
 * Maps the draft's primaryType/descriptors/customPrimaryLabel to the real,
 * camelCase enums for `PUT listing/:id/property-types`'s direct-set path.
 * Returns undefined when `draft.primaryType` isn't set yet — nothing
 * meaningful to send.
 */
export function draftToRealPropertyTypes(draft: IDraftListing):
  | {
      primaryType: IPrimaryPropertyType['type'];
      descriptors: IPropertyDescriptor['type'][];
      customPrimaryLabel?: string;
    }
  | undefined {
  if (!draft.primaryType) return undefined;
  const descriptors = (draft.descriptors ?? []).map((d) => DESCRIPTOR_TO_REAL[d]);
  return {
    primaryType: PRIMARY_TYPE_TO_REAL[draft.primaryType],
    descriptors,
    customPrimaryLabel: draft.primaryType === 'custom' ? draft.customPrimaryLabel : undefined,
  };
}

// ─────────────────────── Reverse: real IListing → IDraftListing ───────────────────────
//
// Used to hydrate the edit wizard from a live `GET listing/:id` when no local
// published-snapshot exists in this browser (e.g. a different browser/device
// published it, or — the flow that actually surfaces this — a sales rep built
// the listing and the client is now opening it for the first time to finish
// and publish it themselves). Best-effort: some real→draft mappings are still
// inherently lossy in the same direction the forward mapper already documents
// (checkInProcess, cancellationPolicy). primaryType/descriptors/
// customPrimaryLabel round-trip cleanly now — see the mapping tables above.
// Missing/unmappable fields are simply left undefined — `missingRequiredFields`
// already surfaces those as normal "before you can publish" prompts rather
// than silently guessing wrong data.

export function reverseCancellationPolicy(policy: IListing['cancellationPolicy']): CancellationTier | undefined {
  if (!policy) return undefined;
  return policy === 'easy' ? 'easy-goin' : policy;
}

function reverseCheckInProcess(process: IListing['checkInProcess']): CheckInProcess | undefined {
  if (!process) return undefined;
  // Forward mapping collapses both 'self-checkin' and 'lockbox' to 'none' —
  // irreversible; 'self-checkin' is the more common case, so default to it.
  // Case-insensitive (F21): the type says 'meetAndGreet' but rows store
  // 'meetandgreet', which loaded as self check-in and then saved as 'none'.
  return String(process).toLowerCase() === 'meetandgreet' ? 'meet-greet' : 'self-checkin';
}

function reverseBookability(process: IListing['reservationProcess']): Bookability {
  return process === 'instant' ? 'instant' : 'request';
}

function reversePrivacy(privacy: IPrivacy | undefined): VisibilityKind[] {
  if (!privacy) return [];
  const out: VisibilityKind[] = [];
  if (privacy.secluded) out.push('secluded');
  if (privacy.home) out.push('home-visible');
  if (privacy.neighbor) out.push('neighbors');
  if (privacy.others) out.push('other-guests');
  return out;
}

function reverseNoise(noise: INoise | undefined): NoiseLevel | undefined {
  if (!noise?.level) return undefined;
  if (noise.level === 'nature') return 'sounds-of-nature';
  return noise.level as NoiseLevel;
}

/** `IVehicle`'s eight booleans back to the wizard's `RvType[]` (F15). Undefined
 *  when the site has no vehicle record at all, so an edit of a listing published
 *  before F15 does not read a row of falses as "the host chose none" — the
 *  wizard's own step gate then asks them, which is the right outcome. */
function reverseVehicles(vehicles: IVehicle | undefined): RvType[] | undefined {
  if (!vehicles) return undefined;
  const chosen = RV_TYPES.filter(({ id }) => !!vehicles[id]).map(({ id }) => id);
  return chosen.length > 0 ? chosen : undefined;
}

function reverseLandOwner(
  landOwner: ILandOwner | undefined,
): Pick<IDraftListing, 'isLandowner' | 'landType' | 'landowner' | 'representsLandowner'> {
  if (!landOwner || landOwner.self !== false) {
    return { isLandowner: true, landType: undefined, landowner: undefined, representsLandowner: undefined };
  }
  return {
    isLandowner: false,
    landType: landOwner.private ? 'private' : 'public',
    landowner: {
      firstName: landOwner.firstName ?? '',
      lastName: landOwner.lastName ?? '',
      phone: landOwner.phone ?? '',
      address: landOwner.address ?? { address: '', city: '', state: '', zipcode: '' },
    },
    representsLandowner: landOwner.represent,
  };
}

/** Real policies back to the wizard: a sentence `draftToRealListingPolicies` wrote
 *  for a checkbox ticks that checkbox again; anything else is the host's own
 *  text. Reading them all back as text made the next save send each ticked
 *  rule twice (once from `customRules`, once from a re-ticked box). */
function reverseRules(policies: IPolicy[] | undefined): Pick<IDraftListing, 'rules' | 'customRules'> {
  const rules: IHouseRules = {
    noSmoking: false,
    noParties: false,
    quietHours: false,
    noFireworks: false,
    noFirearms: false,
  };
  const custom: string[] = [];
  for (const description of (policies ?? []).map((p) => p.description).filter((d): d is string => !!d)) {
    const match = RULE_SENTENCES.find(([, sentence]) => sentence === description.trim());
    if (match) rules[match[0]] = true;
    else custom.push(description);
  }
  return { rules, customRules: custom.join('\n') };
}

/** The editor's crop preview works in 0..1 fractions, but `syncDraftPhotos`
 *  saves crops in pixels and the server keeps no image size to convert back
 *  with. A pixel crop shows the whole photo instead of a wrong slice of it;
 *  the server's crop is untouched, since a saved photo is never re-sent. */
function reverseCrop(p: ISubPhoto): IPhotoMeta['crop'] {
  const crop = { left: p.cropLeft ?? 0, top: p.cropTop ?? 0, right: p.cropRight ?? 1, bottom: p.cropBottom ?? 1 };
  const fractional = Object.values(crop).every((v) => v >= 0 && v <= 1);
  return fractional ? crop : { left: 0, top: 0, right: 1, bottom: 1 };
}

/** Best-effort reconstruction of the wizard draft's editable fields from a live-fetched listing. */
export function realListingToDraft(listing: IListing): Partial<IDraftListing> {
  const site = listing.sites?.[0];
  // Photos are a Listing-level concept going forward (see
  // HostListingDraftService.syncDraftPhotos) — prefer listing.photos, falling
  // back to the first site's photos only for listings published before that
  // change. Deliberately NOT the same fallback as listing-entity.mapper.ts's
  // effectivePhotos(): this list round-trips back through syncDraftPhotos,
  // which can only ever write to one place, so unioning every site's photos
  // (as effectivePhotos does for read-only guest display) would risk losing
  // data on save. See that function's doc comment for the other half of this
  // split.
  const photos = listing.photos && listing.photos.length > 0 ? listing.photos : site?.photos ?? [];

  return {
    primaryType: listing.primaryType ? REAL_PRIMARY_TYPE_TO_WIZARD[listing.primaryType] : undefined,
    descriptors: (listing.descriptors ?? []).map((d) => REAL_DESCRIPTOR_TO_WIZARD[d]),
    customPrimaryLabel: listing.customPrimaryLabel,

    address: listing.address,
    lat: listing.coordinates?.lat,
    lng: listing.coordinates?.lon,
    ...reverseLandOwner(listing.landOwner),

    checkInTime: listing.checkIn,
    checkOutTime: listing.checkOut,
    minNights: listing.minLengthOfStayInDays,
    maxNights: listing.maxLengthOfStayInDays,
    minNoticeHours: listing.reserveDaysInAdvance != null ? listing.reserveDaysInAdvance * 24 : undefined,
    maxNoticeWeeks: listing.bookingWindow != null ? Math.round(listing.bookingWindow / 7) : undefined,
    checkInProcess: reverseCheckInProcess(listing.checkInProcess),
    bookability: reverseBookability(listing.reservationProcess),
    cancellationTier: reverseCancellationPolicy(listing.cancellationPolicy),

    title: listing.name,
    description: listing.description,

    hasHazards: listing.potentialHazards,
    hazardsNote: listing.potentialHazardDescription,

    ...reverseRules(listing.policies),

    amenities: [...new Set((site?.amenities ?? []).map((a) => a.type))] as Amenity[],
    electricityAmps: [
      ...new Set(
        (site?.amenities ?? [])
          .filter((a) => a.type === 'electricity' && a.options)
          .map((a) => `${a.options}A` as ElectricityAmps),
      ),
    ],
    waterHookup: (site?.amenities ?? [])
      .map((a) => (a.type === 'water' ? a.options : undefined))
      .find((o): o is WaterHookup => o === 'direct' || o === 'station'),
    maxRig:
      site && (site.maxWidth || site.maxLength || site.maxHeight)
        ? { width: site.maxWidth ?? 0, length: site.maxLength ?? 0, height: site.maxHeight ?? 0 }
        : undefined,
    guestCapacity: site?.maxGuests,
    visibility: reversePrivacy(site?.privacy),
    noiseLevel: reverseNoise(site?.noise),
    nightlyPrice: site?.price,
    // The other half of F15. Publishing now sends vehicles and tentOnly, so the
    // edit round-trip has to read them back — otherwise opening a published
    // listing in the wizard rebuilt the draft with `vehicleTypes` undefined,
    // `createDraft()`'s all-eight seed would not apply on an existing draft, and
    // saving would write a set the host never chose over the one they had.
    vehicleTypes: reverseVehicles(site?.vehicles),
    tentMode: site?.tentOnly ? 'tents-only' : undefined,

    photos: photos.map((p) => HelperFunctions.getPhotoUrlOriginal(p)),
    photoCaptions: photos.map((p) => p.caption ?? ''),
    photoMeta: photos.map(
      (p): IPhotoMeta => ({
        crop: reverseCrop(p),
        rotation: p.rotation === 90 || p.rotation === 180 || p.rotation === 270 ? p.rotation : 0,
      }),
    ),

    // Must agree with `addOnsFromEntity` in listing-entity.mapper.ts — this is
    // the same server row read for the host's editor rather than the guest's
    // page, and the two had drifted badly. It read `e.label` as the name (the
    // TYPE-key column, so editing a listing renamed "Firewood" to "firewood"),
    // hardcoded `unit: 'per night'` where the guest page said 'per stay', and
    // dropped both `count` and `realExtraId` — so opening a listing in the
    // editor and saving it silently wiped the host's own quantity caps and
    // re-created its extras as new rows.
    addOns: (listing.extras ?? []).map(
      (e, i): IAddOn => ({
        id: e.id != null ? `extra-${e.id}` : `addon-${i}`,
        label: addOnDisplayName(e),
        description: e.description ?? '',
        icon: 'add_circle',
        unit: addOnUnitFromReal(e),
        maxQuantity: addOnCapFromCount(e.count),
        price: e.price ?? 0,
        ...(e.id != null ? { realExtraId: e.id } : {}),
      }),
    ),
  };
}
